import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithRetry, ENDPOINT_TAGS, generateEndpoint } from '../../../core/store/rootApi';
import {
  CategoryGetRequest,
  CategoryGetResponse,
  CategoryParams,
  CategoryWithArticlesGetResponse,
} from '../model/categoryModel';

export const categoryApi = createApi({
  reducerPath: 'categoryApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [
    ENDPOINT_TAGS.CATEGORY,
    ENDPOINT_TAGS.CATEGORIES,
    ENDPOINT_TAGS.CATEGORY_WITH_ARTICLES,
    ENDPOINT_TAGS.CATEGORIES_TREE,
  ],
  endpoints: (builder) => ({
    getCategories: builder.query<CategoryGetResponse[], CategoryGetRequest>({
      query: (params) => ({
        url: '/api/categories',
        params: params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.CATEGORIES as const, id })),
              { type: ENDPOINT_TAGS.CATEGORIES, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.CATEGORIES, id: 'LIST' }],
    }),
    getCategory: builder.query<CategoryGetResponse, CategoryParams>({
      query: ({ id }) => generateEndpoint('/api/categories/:id', { id: id! }),
      providesTags: (result, error, args) => [{ type: ENDPOINT_TAGS.CATEGORY, id: args.id }],
    }),
    getCategoryWithArticles: builder.query<CategoryWithArticlesGetResponse[], CategoryParams>({
      query: ({ id }) => generateEndpoint('/api/categories/:id/with-articles', { id: id! }),
      transformResponse: (
        response: { [key: number]: CategoryWithArticlesGetResponse } | CategoryWithArticlesGetResponse[],
        meta,
        arg,
      ) => {
        if (!Array.isArray(response)) {
          return [response[arg.id as unknown as number]];
        }

        return response;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.CATEGORY_WITH_ARTICLES as const, id })),
              { type: ENDPOINT_TAGS.CATEGORY_WITH_ARTICLES, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.CATEGORY_WITH_ARTICLES, id: 'LIST' }],
    }),
    getCategoryTrees: builder.query<CategoryGetResponse[], CategoryParams>({
      query: ({ id }) => generateEndpoint('/api/categories/:id/tree', { id: id! }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.CATEGORIES_TREE as const, id })),
              { type: ENDPOINT_TAGS.CATEGORIES_TREE, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.CATEGORIES_TREE, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useGetCategoryWithArticlesQuery,
  useGetCategoryTreesQuery,
  useLazyGetCategoryTreesQuery,
} = categoryApi;
